'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

interface HowToUseGuideProps {
  defaultExpanded?: boolean;
  className?: string;
}

const steps = [
  {
    label: 'STEP 01',
    title: '기본 견적 확인',
    color: '#E8723A',
    gradientEnd: '#F09060',
    tipBg: '#FFF8F4',
    tipText: '#B85A2E',
    body: '배송할 물품이 아래 리스트에 있고, 정확한 크기를 알고 있어요 ➔ \'직접 선택\'으로 진행해 주세요\n배송할 물품이 아래 리스트에 없거나, 설명이 필요해요 ➔ \'견적 문의\'로 요청해 주세요',
    tip: '정확한 주소와 엘리베이터 유무를 확인해주세요',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-6 h-6">
        <rect x="8" y="6" width="32" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path d="M16 6V2M32 6V2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M8 16h32" stroke="currentColor" strokeWidth="2.5" />
        <path d="M20 26l3 3 6-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'STEP 02',
    title: '일정 선택 및 결제',
    color: '#1A3A5C',
    gradientEnd: '#2E5F8F',
    tipBg: '#F4F7FB',
    tipText: '#1A3A5C',
    body: '원하는 배송 일자와 시간을 선택 (할인시간대 확인) 한 뒤,\n출발지 연락처와 출입방법 등을 빠짐없이 기재 후 원하는 방법으로 결제하세요',
    tip: '결제 완료 즉시 결제 내역을 문자로 보내드려요. 취소 및 수정은 배송 24시간 전에 언제든 가능해요',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-6 h-6">
        <path d="M6 18l18-12 18 12" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" fill="none" />
        <path d="M10 16v18a4 4 0 004 4h20a4 4 0 004-4V16" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <rect x="18" y="26" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path d="M24 26v12" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
      </svg>
    ),
  },
  {
    label: 'STEP 03',
    title: '배송 실시간 안내',
    color: '#22A06B',
    gradientEnd: '#36C98E',
    tipBg: undefined,
    tipText: undefined,
    body: '도착 전 안내, 물품 수거 전 영상,\n배송 완료 영상을 실시간 문자로 알려드려요',
    tip: null,
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-6 h-6">
        <rect x="12" y="4" width="24" height="40" rx="5" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <circle cx="24" cy="38" r="2" fill="currentColor" />
        <path d="M18 14h12M18 20h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M30 24l-4 4-2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function HowToUseGuide({ defaultExpanded = true, className }: HowToUseGuideProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`bg-white rounded-2xl border border-[rgba(26,58,92,0.06)] shadow-[0_1px_4px_rgba(26,58,92,0.06)] overflow-hidden ${className ?? ''}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-5 py-[18px] cursor-pointer select-none"
      >
        <Image
          src="/images/donkey_icon.png"
          alt="당나귀"
          width={32}
          height={32}
          className="w-8 h-8 rounded-[10px] flex-shrink-0"
        />
        <span className="flex-1 text-left text-[15px] font-bold text-[#1A3A5C] tracking-[-0.03em]">
          당나귀배송 이용 가이드
        </span>
        <svg
          className="w-[18px] h-[18px] text-[#A0A8B4] transition-transform duration-300 flex-shrink-0"
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Divider */}
      {expanded && <div className="h-px bg-[#F0F1F4] mx-4" />}

      {/* Content */}
      <div
        aria-hidden={!expanded}
        className="transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          maxHeight: expanded ? '600px' : '0px',
          opacity: expanded ? 1 : 0,
          overflow: 'hidden',
        }}
      >
        <div className="px-4 pt-2 pb-5">
          {steps.map((step, i) => (
            <div key={step.label} className="flex gap-3.5">
              {/* Left: icon + connector */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className="w-11 h-11 rounded-[14px] flex items-center justify-center text-white"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}, ${step.gradientEnd})`,
                    boxShadow: `0 4px 12px ${step.color}33`,
                    transitionDelay: `${150 + i * 120}ms`,
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(12px)',
                    transition: 'opacity 0.5s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.4,0,0.2,1)',
                  }}
                >
                  {step.icon}
                </div>
                {i < steps.length - 1 && (
                  <div
                    className="w-0.5 flex-1 my-1 opacity-70"
                    style={{
                      backgroundImage: `repeating-linear-gradient(to bottom, #D8DCE2 0px, #D8DCE2 4px, transparent 4px, transparent 8px)`,
                    }}
                  />
                )}
              </div>

              {/* Right: content */}
              <div
                className={`flex-1 ${i < steps.length - 1 ? 'pb-4' : 'pb-1'}`}
                style={{
                  transitionDelay: `${150 + i * 120}ms`,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(12px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.4,0,0.2,1)',
                }}
              >
                <span
                  className="text-[11px] font-bold tracking-[0.04em]"
                  style={{ color: step.color }}
                >
                  {step.label}
                </span>
                <h3 className="text-[16px] font-bold text-[#1D2939] tracking-[-0.03em] mt-0.5 mb-1">
                  {step.title}
                </h3>
                <p className="text-[13px] leading-[1.65] text-[#505968] whitespace-pre-line">
                  {step.body}
                </p>
                {step.tip && (
                  <div
                    className="mt-2 px-3 py-2 rounded-lg text-[12.5px] font-medium leading-[1.6]"
                    style={{
                      backgroundColor: step.tipBg,
                      borderLeft: `3px solid ${step.color}`,
                      color: step.tipText,
                    }}
                  >
                    💡 {step.tip}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
